import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Typography,
  Tooltip,
  TableSortLabel,
  TablePagination,
  TextField,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const initialWishlist = [
  { _id: "w1", title: "Gold Necklace", image: "https://via.placeholder.com/100", price: 1200, category: "Necklaces" },
  { _id: "w2", title: "Diamond Ring", image: "https://via.placeholder.com/100", price: 3000, category: "Rings" },
  { _id: "w3", title: "Silver Earrings", image: "https://via.placeholder.com/100", price: 500, category: "Earrings" },
  { _id: "w4", title: "Platinum Bracelet", image: "https://via.placeholder.com/100", price: 2500, category: "Bracelets" },
  { _id: "w5", title: "Pearl Necklace", image: "https://via.placeholder.com/100", price: 1800, category: "Necklaces" },
];

const Wishlist = () => {
  const [wishlist, setWishlist] = useState(initialWishlist);
  const [cart, setCart] = useState([]);
  const [filteredWishlist, setFilteredWishlist] = useState(initialWishlist);
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter wishlist based on search
  useEffect(() => {
    const filtered = wishlist.filter(
      (item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredWishlist(filtered);
    setPage(0);
  }, [searchTerm, wishlist]);

  // Sort function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sorted = [...filteredWishlist].sort((a, b) => {
      if (typeof a[property] === "string") {
        return isAsc
          ? a[property].localeCompare(b[property])
          : b[property].localeCompare(a[property]);
      }
      return isAsc ? a[property] - b[property] : b[property] - a[property];
    });
    setFilteredWishlist(sorted);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Remove item from wishlist
  const handleRemove = (id) => {
    setWishlist((prev) => prev.filter((item) => item._id !== id));
  };

  // Move item to cart
  const handleMoveToCart = (item) => {
    setCart((prev) => [...prev, item]);
    handleRemove(item._id);
    alert(`${item.title} moved to cart!`); // optional feedback
  };

  return (
    <Paper sx={{ padding: 2, marginTop: 3 }}>
      <Typography variant="h6" gutterBottom>
        Wishlist
      </Typography>

      {/* Search */}
      <TextField
        label="Search by Title or Category"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filteredWishlist.length === 0 ? (
        <Typography align="center" sx={{ mt: 3 }}>
          No products in wishlist
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    { id: "customer_id", label: "Customer Id" },
                    { id: "image", label: "Image" },
                    { id: "title", label: "Title" },
                    { id: "category", label: "Category" },
                    { id: "price", label: "Price" },
                    { id: "actions", label: "Actions" },
                  ].map((head) => (
                    <TableCell key={head.id}>
                      {head.id !== "image" && head.id !== "actions" ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : "asc"}
                          onClick={() => handleSort(head.id)}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredWishlist
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>{item.customer_id}</TableCell>
                      <TableCell>
                        {item.image && (
                          <Tooltip
                            title={
                              <img
                                src={item.image}
                                alt={item.title}
                                style={{ width: 200 }}
                              />
                            }
                          >
                            <Avatar
                              variant="square"
                              src={item.image}
                              alt={item.title}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{`₹${item.price.toLocaleString()}`}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => handleMoveToCart(item)}
                        >
                          Move to Cart
                        </Button>
                        <IconButton onClick={() => handleRemove(item._id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredWishlist.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}
    </Paper>
  );
};

export default Wishlist;
